import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { api, ApiError } from "@/lib/api";
import { Panel } from "@/components/ui/Panel";
import { Metric } from "@/components/ui/Metric";
import { DataTable } from "@/components/ui/DataTable";
import { EmptyState } from "@/components/ui/EmptyState";
import { Pagination } from "@/components/ui/Pagination";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { useToast } from "@/features/toast/ToastProvider";
import { useConfirm } from "@/features/confirm/ConfirmProvider";
import { formatDate, formatMoney } from "@bookboost/shared/format";

interface PayoutRow {
  id: string;
  user_id: string;
  commission_total: number;
  cash_portion: number;
  equity_portion: number;
  currency: string;
  status: "approved" | "paid";
  earned_at: string | null;
  paid_at: string | null;
  users: { email: string; full_name: string | null; salary_currency: string | null } | null;
  deals: { deal_name: string | null } | null;
  paid_by_user: { email: string; full_name: string | null } | null;
}

const payoutsSearch = z.object({
  show: z.enum(["approved", "paid"]).optional(),
  page: z.coerce.number().int().min(1).optional(),
});

interface CurrencyTotal {
  currency: string;
  total: number;
  count: number;
}

export const Route = createFileRoute("/admin/payouts")({
  validateSearch: payoutsSearch,
  component: PayoutsPage,
});

// Matches the .export-link treatment used on /admin/commissions.
const payrollLinkStyle: React.CSSProperties = {
  marginLeft: "auto",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  color: "var(--muted)",
  fontSize: 12.5,
  padding: "6px 10px",
  borderRadius: 6,
};

function PayoutsPage() {
  const { show, page } = Route.useSearch();
  const view = show === "paid" ? "paid" : "approved";
  const navigate = Route.useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "payouts", view, page],
    queryFn: () =>
      api.get<{
        rows: PayoutRow[];
        status: "approved" | "paid";
        total: number;
        page: number;
        pageSize: number;
        totalsByCurrency: CurrencyTotal[];
      }>("/api/finance/queue", {
        searchParams: { show: view, page },
      }),
  });

  // Group by rep so admin sees per-disbursement totals, not line items.
  const grouped = useMemo(() => {
    const byUser = new Map<string, { rep: PayoutRow["users"]; rows: PayoutRow[] }>();
    for (const r of data?.rows ?? []) {
      const k = r.user_id;
      const entry = byUser.get(k) ?? { rep: r.users, rows: [] };
      entry.rows.push(r);
      byUser.set(k, entry);
    }
    return [...byUser.entries()]
      .map(([userId, { rep, rows }]) => {
        const totalByCcy = new Map<string, number>();
        const cashByCcy = new Map<string, number>();
        const equityByCcy = new Map<string, number>();
        for (const r of rows) {
          totalByCcy.set(r.currency, (totalByCcy.get(r.currency) ?? 0) + r.commission_total);
          cashByCcy.set(r.currency, (cashByCcy.get(r.currency) ?? 0) + r.cash_portion);
          equityByCcy.set(r.currency, (equityByCcy.get(r.currency) ?? 0) + r.equity_portion);
        }
        return {
          userId,
          rep,
          rows,
          totalByCcy: [...totalByCcy.entries()],
          cashByCcy: [...cashByCcy.entries()],
          equityByCcy: [...equityByCcy.entries()],
          ids: rows.map((r) => r.id),
          sortValue: [...totalByCcy.values()].reduce((a, b) => a + b, 0),
        };
      })
      .sort((a, b) => b.sortValue - a.sortValue);
  }, [data]);

  const allIds = useMemo(() => (data?.rows ?? []).map((r) => r.id), [data]);
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id));
  const someSelected = allIds.some((id) => selected.has(id));

  const bulkMarkPaid = useMutation({
    mutationFn: (ids: string[]) => api.post<{ ok: true; updated: number }>("/api/payouts/bulk-mark-paid", { ids }),
    onSuccess: (res) => {
      toast.ok(`Marked ${res.updated} commission${res.updated === 1 ? "" : "s"} paid.`);
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ["admin"] });
      qc.invalidateQueries({ queryKey: ["finance"] });
    },
    onError: (err) => toast.err(err instanceof ApiError ? err.message : "Couldn't mark paid."),
  });
  const bulkMarkUnpaid = useMutation({
    mutationFn: (ids: string[]) => api.post<{ ok: true; updated: number }>("/api/payouts/bulk-mark-unpaid", { ids }),
    onSuccess: (res) => {
      toast.ok(`Reverted ${res.updated} commission${res.updated === 1 ? "" : "s"} to earned.`);
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ["admin"] });
      qc.invalidateQueries({ queryKey: ["finance"] });
    },
    onError: (err) => toast.err(err instanceof ApiError ? err.message : "Couldn't revert."),
  });

  async function onMarkSelectedPaid() {
    const ids = [...selected];
    if (ids.length === 0) return;
    const ok = await confirm({
      title: "Mark commissions paid",
      prompt: `Mark ${ids.length} commission${ids.length === 1 ? "" : "s"} as paid?`,
      confirmLabel: "Mark paid",
    });
    if (ok) bulkMarkPaid.mutate(ids);
  }
  async function onMarkSelectedUnpaid() {
    const ids = [...selected];
    if (ids.length === 0) return;
    const ok = await confirm({
      title: "Revert to earned",
      prompt: `Revert ${ids.length} commission${ids.length === 1 ? "" : "s"} back to earned?`,
      tone: "destructive",
      confirmLabel: "Revert",
    });
    if (ok) bulkMarkUnpaid.mutate(ids);
  }

  // Summed across the whole queue server-side, not just the current page.
  const totals = data?.totalsByCurrency ?? [];

  return (
    <>
      <h1>Payouts</h1>

      <Panel>
        <nav style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <Link
            to="/admin/payouts"
            search={{ show: undefined }}
            style={{ fontWeight: view === "approved" ? 600 : 400 }}
            onClick={() => setSelected(new Set())}
          >
            Pending ({view === "approved" ? data?.total ?? 0 : "—"})
          </Link>
          <Link
            to="/admin/payouts"
            search={{ show: "paid" }}
            style={{ fontWeight: view === "paid" ? 600 : 400 }}
            onClick={() => setSelected(new Set())}
          >
            Paid
          </Link>
          {/* Plain anchor so the sb-* cookies flow with the download. */}
          <a href={`/api/export/payroll.csv?status=${view}`} style={payrollLinkStyle}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
              <path d="M7 2v7m0 0l-3-3m3 3l3-3M2 12h10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Payroll CSV
          </a>
        </nav>
      </Panel>

      {totals.length > 0 && (
        <div className={`grid cols-${Math.min(totals.length, 4)}`}>
          {totals.map((t) => (
            <Metric
              key={t.currency}
              label={view === "paid" ? `Paid · ${t.currency}` : `Pending · ${t.currency}`}
              value={formatMoney(t.total, t.currency)}
              sub={`${t.count} commission${t.count === 1 ? "" : "s"}`}
              tone={view === "approved" ? "accent" : "default"}
            />
          ))}
        </div>
      )}

      {someSelected && (
        <Panel variant="ok">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
            <span><strong>{selected.size}</strong> commissions selected</span>
            <div style={{ display: "flex", gap: 8 }}>
              {view === "approved" ? (
                <button className="primary" disabled={bulkMarkPaid.isPending} onClick={onMarkSelectedPaid}>
                  {bulkMarkPaid.isPending ? "Marking…" : "Mark selected paid"}
                </button>
              ) : (
                <button
                  style={{ color: "var(--err)", borderColor: "var(--err)" }}
                  disabled={bulkMarkUnpaid.isPending}
                  onClick={onMarkSelectedUnpaid}
                >
                  Revert to earned
                </button>
              )}
              <button type="button" onClick={() => setSelected(new Set())}>Clear</button>
            </div>
          </div>
        </Panel>
      )}

      {isLoading ? (
        <p className="lede">Loading…</p>
      ) : error ? (
        <Panel variant="err">
          <p>{error instanceof Error ? error.message : "Failed to load."}</p>
        </Panel>
      ) : grouped.length === 0 ? (
        <Panel>
          <EmptyState
            icon="payouts"
            title={view === "paid" ? "No paid commissions" : "Nothing pending"}
            body={view === "paid" ? "Mark commissions as paid to see them here." : "Approve commissions first to queue them here."}
          />
        </Panel>
      ) : (
        grouped.map((group) => {
          const allRepSelected = group.ids.every((id) => selected.has(id));
          const someRepSelected = group.ids.some((id) => selected.has(id));
          return (
            <Panel key={group.userId} padding="tight">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderBottom: "1px solid var(--hairline)" }}>
                <div>
                  <strong>{group.rep?.full_name ?? group.rep?.email ?? "—"}</strong>
                  {group.rep?.full_name && (
                    <span className="muted" style={{ fontSize: 11, marginLeft: 8, fontFamily: "var(--font-mono)" }}>
                      {group.rep.email}
                    </span>
                  )}
                  <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                    {group.rows.length} commission{group.rows.length === 1 ? "" : "s"} · Total{" "}
                    {group.totalByCcy.map(([ccy, t]) => formatMoney(t, ccy)).join(" + ")}
                  </div>
                </div>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                  <input
                    type="checkbox"
                    checked={allRepSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = !allRepSelected && someRepSelected;
                    }}
                    onChange={(e) => {
                      setSelected((prev) => {
                        const next = new Set(prev);
                        if (e.target.checked) group.ids.forEach((id) => next.add(id));
                        else group.ids.forEach((id) => next.delete(id));
                        return next;
                      });
                    }}
                  />
                  select all
                </label>
              </div>
              <DataTable>
                <table className="table-cards">
                  <thead>
                    <tr>
                      <th style={{ width: 32 }}></th>
                      <th>Earned</th>
                      <th>Deal</th>
                      <th>Status</th>
                      <th>Cash</th>
                      <th>Equity</th>
                      <th>Total</th>
                      {view === "paid" && <th>Paid</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {group.rows.map((r) => (
                      <tr key={r.id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selected.has(r.id)}
                            onChange={(e) => {
                              setSelected((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(r.id);
                                else next.delete(r.id);
                                return next;
                              });
                            }}
                          />
                        </td>
                        <td data-label="Earned" className="muted">{formatDate(r.earned_at)}</td>
                        <td data-label="Deal">{r.deals?.deal_name ?? "—"}</td>
                        <td data-label="Status"><StatusBadge status={r.status} /></td>
                        <td data-label="Cash">{formatMoney(r.cash_portion, r.currency)}</td>
                        <td data-label="Equity">{formatMoney(r.equity_portion, r.currency)}</td>
                        <td data-label="Total"><strong>{formatMoney(r.commission_total, r.currency)}</strong></td>
                        {view === "paid" && (
                          <td data-label="Paid" className="muted">
                            {formatDate(r.paid_at)}
                            {r.paid_by_user && <div style={{ fontSize: 11 }}>by {r.paid_by_user.full_name ?? r.paid_by_user.email}</div>}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </DataTable>
            </Panel>
          );
        })
      )}

      {allIds.length > 0 && (
        <Panel padding="tight">
          <div style={{ padding: "8px 16px", display: "flex", alignItems: "center", gap: 12 }}>
            <input
              type="checkbox"
              checked={allSelected}
              ref={(el) => { if (el) el.indeterminate = !allSelected && someSelected; }}
              onChange={(e) => setSelected(e.target.checked ? new Set(allIds) : new Set())}
            />
            <span className="muted" style={{ fontSize: 12 }}>
              Select all visible ({allIds.length})
            </span>
          </div>
        </Panel>
      )}

      {data && data.total > 0 && (
        <Panel padding="tight">
          <Pagination
            page={data.page}
            pageSize={data.pageSize}
            total={data.total}
            onPage={(p) => navigate({ search: { show, page: p } })}
          />
        </Panel>
      )}
    </>
  );
}
